import { useMemo, useState } from 'react';

const formatNumber = (value, decimals = 2) => {
    const numeric = Number(value);
    if (value === null || value === undefined || Number.isNaN(numeric)) {
        return '0';
    }
    if (numeric === 0) {
        return '0';
    }
    return numeric.toLocaleString('fr-FR', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });
};

const getMovementKey = (movement) => {
    const stockableId = movement?.stockable_id ?? movement?.stockable?.id ?? movement?.item_id ?? movement?.ingredient_id ?? movement?.id;
    const stockableType = movement?.stockable_type ?? movement?.stockable?.type ?? movement?.item_type ?? movement?.ingredient_type;
    return `${stockableType || 'unknown'}-${stockableId}`;
};

const parseMovementDate = (movement) => {
    const raw = movement?.movement_date || movement?.created_at || movement?.purchase_date || movement?.date || null;
    return raw ? new Date(raw) : null;
};

const isConsumedMovement = (movement) => String(movement?.type || '').toLowerCase() === 'consumption';
const isPurchaseMovement = (movement) => String(movement?.type || '').toLowerCase() === 'purchase';

const getReportPeriod = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const week = Math.ceil((date.getDate() + new Date(year, date.getMonth(), 1).getDay()) / 7);
    return { year, month, week };
};

const buildGroupedStats = (movements) => {
    const byItem = new Map();

    const parseQuantity = (movement) => {
        const raw = movement?.quantity ?? movement?.qty ?? 0;
        if (raw === null || raw === undefined || raw === '') return 0;
        const value = Number(String(raw).toString().replace(/\s/g, '').replace(/,/g, '.'));
        return Number.isFinite(value) ? value : 0;
    };

    for (const movement of movements) {
        const date = parseMovementDate(movement);
        if (!date) continue;

        const key = getMovementKey(movement);
        const stockable = movement?.stockable || {};
        const name = stockable?.name || movement?.item_name || movement?.name || movement?.label || 'Inconnu';
        const itemType = movement?.stockable_type || movement?.item_type || movement?.ingredient_type || 'ingredient';
        const service = movement?.service || null;
        const quantity = parseQuantity(movement);
        const movementType = String(movement?.type || '').toLowerCase();
        const unitName = stockable?.unit?.name || stockable?.unit || movement?.unit?.name || movement?.unit || movement?.unit_name || null;

        if (!byItem.has(key)) {
            byItem.set(key, {
                key,
                id: stockable?.id || movement?.stockable_id || movement?.item_id || movement?.ingredient_id || null,
                name,
                itemType,
                service,
                movements: [],
                totalOut: 0,
                totalIn: 0,
                dailyOut: new Map(),
                weeklyIn: new Map(),
                weeklyOut: new Map(),
                monthlyIn: new Map(),
                monthlyOut: new Map(),
                lastMovementDate: date,
                currentStock: stockable?.stock_quantity ?? stockable?.stock ?? null,
                unit: unitName,
            });
        }

        const item = byItem.get(key);
        item.movements.push(movement);
        item.lastMovementDate = item.lastMovementDate < date ? date : item.lastMovementDate;

        const dayKey = date.toISOString().slice(0, 10);
        const { year, month, week } = getReportPeriod(date);
        const weekKey = `${year}-W${String(week).padStart(2, '0')}`;
        const monthKey = `${year}-${String(month).padStart(2, '0')}`;

        let inQty = 0;
        let outQty = 0;

        if (movementType === 'purchase') {
            inQty = Math.abs(quantity);
        } else if (movementType === 'consumption') {
            outQty = Math.abs(quantity);
        } else if (movementType === 'adjustment') {
            if (quantity >= 0) inQty = quantity;
            else outQty = Math.abs(quantity);
        } else {
            if (quantity >= 0) inQty = quantity;
            else outQty = Math.abs(quantity);
        }

        if (inQty > 0) {
            item.totalIn += inQty;
            item.weeklyIn.set(weekKey, (item.weeklyIn.get(weekKey) || 0) + inQty);
            item.monthlyIn.set(monthKey, (item.monthlyIn.get(monthKey) || 0) + inQty);
        }

        if (outQty > 0) {
            item.totalOut += outQty;
            item.dailyOut.set(dayKey, (item.dailyOut.get(dayKey) || 0) + outQty);
            item.weeklyOut.set(weekKey, (item.weeklyOut.get(weekKey) || 0) + outQty);
            item.monthlyOut.set(monthKey, (item.monthlyOut.get(monthKey) || 0) + outQty);
        }
    }

    return Array.from(byItem.values()).map((item) => {
        const weeksOut = Math.max(1, item.weeklyOut.size);
        const monthsOut = Math.max(1, item.monthlyOut.size);
        const weeksIn = Math.max(1, item.weeklyIn.size);
        const monthsIn = Math.max(1, item.monthlyIn.size);

        const avgWeeklyOut = item.totalOut / weeksOut;
        const avgMonthlyOut = item.totalOut / monthsOut;
        const avgWeeklyIn = item.totalIn / weeksIn;
        const avgMonthlyIn = item.totalIn / monthsIn;

        const dailyOutValues = Array.from(item.dailyOut.values());
        const maxDailyOut = dailyOutValues.length ? Math.max(...dailyOutValues) : 0;

        const computedStock = item.currentStock ?? (item.totalIn - item.totalOut);

        return {
            ...item,
            currentStock: computedStock,
            avgWeeklyOut,
            avgMonthlyOut,
            avgWeeklyIn,
            avgMonthlyIn,
            maxDailyOut,
            averageDailyOut: item.totalOut / Math.max(1, item.dailyOut.size),
            lastUsed: item.lastMovementDate ? item.lastMovementDate.toLocaleDateString('fr-FR') : '—',
            recommendedFridayOrder: item.totalOut && computedStock != null
                ? Math.max(0, Math.round(Math.max(0, avgWeeklyOut * 1.5 - computedStock)))
                : 0,
        };
    });
};

const StockTrendsTab = ({ ingredients = [], consumables = [], movements = [] }) => {
    const [itemTypeFilter, setItemTypeFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');

    const selectedItems = useMemo(() => {
        const sources = [...ingredients, ...consumables];
        return new Map(sources.map((item) => [String(item.id), item]));
    }, [ingredients, consumables]);

    const stats = useMemo(() => {
        const relevant = (movements || []).filter((movement) => {
            const isRelevantType = ['purchase', 'consumption'].includes(String(movement?.type || '').toLowerCase());
            if (!isRelevantType) return false;
            if (itemTypeFilter !== 'all') {
                const movementType = String(movement?.stockable_type || movement?.item_type || '').toLowerCase();
                return movementType === itemTypeFilter;
            }
            return true;
        });

        const normalized = buildGroupedStats(relevant);
        const statsByKey = new Map(normalized.map((item) => [item.key, item]));

        const sources = [...ingredients, ...consumables];
        for (const source of sources) {
            const sourceType = String(source?.stockable_type || source?.item_type || source?.type || (consumables.includes(source) ? 'consumable' : 'ingredient') || 'ingredient').toLowerCase();
            if (itemTypeFilter !== 'all' && sourceType !== itemTypeFilter) continue;

            const sourceKey = `${sourceType}-${String(source?.id ?? source?.ingredient_id ?? source?.stockable_id ?? 'unknown')}`;
            if (statsByKey.has(sourceKey)) continue;

            statsByKey.set(sourceKey, {
                key: sourceKey,
                id: source?.id ?? source?.ingredient_id ?? source?.stockable_id ?? null,
                name: source?.name || source?.label || source?.item_name || 'Inconnu',
                itemType: sourceType,
                service: source?.service || null,
                movements: [],
                totalOut: 0,
                totalIn: 0,
                dailyOut: new Map(),
                weeklyIn: new Map(),
                weeklyOut: new Map(),
                monthlyIn: new Map(),
                monthlyOut: new Map(),
                lastMovementDate: null,
                currentStock: source?.stock_quantity ?? source?.stock ?? source?.quantity ?? 0,
                unit: source?.unit?.name || source?.unit || source?.unit_name || '',
                avgWeeklyOut: 0,
                avgMonthlyOut: 0,
                avgWeeklyIn: 0,
                avgMonthlyIn: 0,
                maxDailyOut: 0,
                averageDailyOut: 0,
                recommendedFridayOrder: 0,
                lastUsed: '—',
            });
        }

        return Array.from(statsByKey.values())
            .map((item) => {
                const product = selectedItems.get(String(item.id));
                return {
                    ...item,
                    currentStock: item.currentStock ?? 0,
                    unit: item.unit || product?.unit || product?.unit?.name || product?.unit_name || '',
                };
            })
            .filter((item) => item.name.toLowerCase().includes(searchTerm.trim().toLowerCase()));
    }, [movements, itemTypeFilter, searchTerm, selectedItems, ingredients, consumables]);

    return (
        <div className="space-y-6">
            <section className="rounded-3xl border border-slate-200 bg-slate-50 p-6 shadow-sm">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                    <div>
                        <p className="text-sm uppercase tracking-[0.35em] text-slate-500">Analyse de tendance</p>
                        <h2 className="text-3xl font-semibold text-slate-900">Tendances de consommation & approvisionnement</h2>
                        <p className="mt-2 text-sm text-slate-600">
                            Moyenne des entrées et sorties par semaine/mois, consommation jour par jour et recommandations d’achats pour le vendredi.
                        </p>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3">
                        <label className="block text-sm font-medium text-slate-800">
                            Filtrer par type
                            <select
                                value={itemTypeFilter}
                                onChange={(event) => setItemTypeFilter(event.target.value)}
                                className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900"
                            >
                                <option value="all">Tous</option>
                                <option value="ingredient">Ingrédients</option>
                                <option value="consumable">Consommables</option>
                            </select>
                        </label>
                        <label className="block text-sm font-medium text-slate-800">
                            Rechercher
                            <input
                                type="search"
                                value={searchTerm}
                                onChange={(event) => setSearchTerm(event.target.value)}
                                placeholder="Nom d’article..."
                                className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900"
                            />
                        </label>
                    </div>
                </div>
            </section>

            <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200 text-left text-sm text-slate-700">
                        <thead className="bg-slate-100 text-slate-900">
                            <tr>
                                <th className="px-4 py-3 font-medium">Article</th>
                                <th className="px-4 py-3 font-medium">Stock actuel</th>
                                <th className="px-4 py-3 font-medium">Sortie moyenne / semaine</th>
                                <th className="px-4 py-3 font-medium">Sortie moyenne / mois</th>
                                <th className="px-4 py-3 font-medium">Entrée moyenne / semaine</th>
                                <th className="px-4 py-3 font-medium">Entrée moyenne / mois</th>
                                <th className="px-4 py-3 font-medium">Sortie max / jour</th>
                                <th className="px-4 py-3 font-medium">Commande recommandée vendredi</th>
                                <th className="px-4 py-3 font-medium">Dernier mouvement</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 bg-slate-50">
                            {stats.length === 0 ? (
                                <tr>
                                    <td colSpan="9" className="px-4 py-6 text-center text-sm text-slate-500">
                                        Aucune donnée de tendance disponible pour ces filtres.
                                    </td>
                                </tr>
                            ) : stats.map((item) => (
                                <tr key={item.key} className="hover:bg-slate-100">
                                    <td className="px-4 py-3 font-medium text-slate-900">{item.name}</td>
                                    <td className="px-4 py-3 text-slate-700">{formatNumber(item.currentStock, 0)}{item.unit ? ` ${item.unit}` : ''}</td>
                                    <td className="px-4 py-3 text-slate-700">{formatNumber(item.avgWeeklyOut, 2)}{item.unit ? ` ${item.unit}` : ''}</td>
                                    <td className="px-4 py-3 text-slate-700">{formatNumber(item.avgMonthlyOut, 2)}{item.unit ? ` ${item.unit}` : ''}</td>
                                    <td className="px-4 py-3 text-slate-700">{formatNumber(item.avgWeeklyIn, 2)}{item.unit ? ` ${item.unit}` : ''}</td>
                                    <td className="px-4 py-3 text-slate-700">{formatNumber(item.avgMonthlyIn, 2)}{item.unit ? ` ${item.unit}` : ''}</td>
                                    <td className="px-4 py-3 text-slate-700">{formatNumber(item.maxDailyOut, 2)}{item.unit ? ` ${item.unit}` : ''}</td>
                                    <td className="px-4 py-3 text-slate-700">{formatNumber(item.recommendedFridayOrder, 0)}{item.unit ? ` ${item.unit}` : ''}</td>
                                    <td className="px-4 py-3 text-slate-700">{item.lastUsed}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
    );
};

export default StockTrendsTab;
